/**
 * Google Tag Manager Integration Module
 *
 * Handles GTM detection, loading, and event tracking with attribution data.
 */

import { TrackingData } from '@peptalk/shared';
import { getTrackingData } from './tracking';

// Internal event names (used in code)
export type GTMEventName =
  | 'widget_loaded'
  | 'chat_started'
  | 'lead_generated'
  | 'form_filling_started'
  | 'form_submitted'
  | 'enquiry_submitted'
  | 'widget_message_submitted';

// Event names are pushed directly to dataLayer in snake_case format
// These follow GTM naming conventions (letters, numbers, underscores only)
// In GTM, create Custom Event Triggers matching these exact names

// Pre-captured data for delayed event firing (preserves original timestamp and attribution)
export interface GTMCapturedData {
  timestamp: number;
  attribution: TrackingData;
}

// Attribution data structure for GTM events
export interface GTMAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  msclkid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  li_fat_id?: string;
  rdt_cid?: string;
  dclid?: string;
  impact_code?: string;
}

// Event payload structure
export interface GTMEventPayload {
  event: GTMEventName; // GTM-compliant snake_case event name (e.g., "form_filling_started")
  widget_instance_id: string;
  form_session_id?: string; // Correlates form_filling_started with form_submitted
  timestamp: number;
  attribution: GTMAttribution;
  person?: {
    name?: string;
    email?: string;
  };
  page: {
    url: string;
    title: string;
    referrer?: string;
  };
}

// Configuration for GTM module
export interface GTMConfig {
  enabled: boolean;
  containerId?: string;
  loadIfMissing: boolean;
}

// Extend Window type for dataLayer
declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

// Module state
interface GTMState {
  initialized: boolean;
  gtmAvailable: boolean;
  eventQueue: GTMEventPayload[];
  config: GTMConfig;
}

const state: GTMState = {
  initialized: false,
  gtmAvailable: false,
  eventQueue: [],
  config: {
    enabled: false,
    loadIfMissing: false,
  },
};

/**
 * Check if GTM/dataLayer is available on the page
 */
export function isGTMAvailable(): boolean {
  return typeof window !== 'undefined' && Array.isArray(window.dataLayer);
}

/**
 * Initialize GTM integration
 * Must be called once during widget initialization
 */
export function initializeGTM(config: Partial<GTMConfig> = {}): void {
  if (state.initialized) {
    console.warn('[Widget GTM] Already initialized');
    return;
  }

  state.config = {
    enabled: config.enabled ?? false,
    loadIfMissing: config.loadIfMissing ?? false,
    containerId: config.containerId,
  };

  console.log('[Widget GTM] Initializing with config:', state.config);

  if (!state.config.enabled) {
    console.log('[Widget GTM] GTM disabled, skipping initialization');
    state.initialized = true;
    return;
  }

  // Check for existing GTM
  state.gtmAvailable = isGTMAvailable();
  console.log('[Widget GTM] GTM already available:', state.gtmAvailable);

  if (
    !state.gtmAvailable &&
    state.config.loadIfMissing &&
    state.config.containerId
  ) {
    console.log(
      '[Widget GTM] Loading GTM script for container:',
      state.config.containerId
    );
    loadGTMScript(state.config.containerId);
  } else if (state.gtmAvailable) {
    // GTM already available, flush any queued events
    flushEventQueue();
  }

  state.initialized = true;
}

/**
 * Load GTM script dynamically
 */
function loadGTMScript(containerId: string): void {
  if (typeof window === 'undefined') return;

  // Initialize dataLayer if not present
  window.dataLayer = window.dataLayer || [];

  // Push GTM start event
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  // Create script element
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;

  script.onload = () => {
    state.gtmAvailable = true;
    flushEventQueue();
  };

  script.onerror = () => {
    console.warn('[Widget GTM] Failed to load GTM script');
    state.gtmAvailable = false;
  };

  // Insert script at the beginning
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
}

/**
 * Convert internal tracking data to GTM attribution format
 */
function mapTrackingToAttribution(trackingData: TrackingData): GTMAttribution {
  return {
    utm_source: trackingData.utmSource,
    utm_medium: trackingData.utmMedium,
    utm_campaign: trackingData.utmCampaign,
    utm_term: trackingData.utmTerm,
    utm_content: trackingData.utmContent,
    gclid: trackingData.gclid,
    msclkid: trackingData.msclkid,
    gbraid: trackingData.gbraid,
    wbraid: trackingData.wbraid,
    fbclid: trackingData.fbclid,
    li_fat_id: trackingData.liFatId,
    rdt_cid: trackingData.rdtCid,
    dclid: trackingData.dclid,
    impact_code: trackingData.impactCode,
  };
}

/**
 * Build event payload with attribution data
 * Supports pre-captured data for delayed event firing (preserves original timestamp/attribution)
 */
function buildEventPayload(
  eventName: GTMEventName,
  instanceId: string,
  personData?: { name?: string; email?: string },
  capturedData?: GTMCapturedData,
  formSessionId?: string
): GTMEventPayload {
  // Use pre-captured data if provided, otherwise get current data
  const trackingData = capturedData?.attribution ?? getTrackingData();
  const timestamp = capturedData?.timestamp ?? Date.now();

  const payload: GTMEventPayload = {
    event: eventName, // Use snake_case name directly (GTM-compliant)
    widget_instance_id: instanceId,
    timestamp,
    attribution: mapTrackingToAttribution(trackingData),
    person: personData,
    page: {
      url: typeof window !== 'undefined' ? window.location.href : '',
      title: typeof document !== 'undefined' ? document.title : '',
      referrer:
        typeof document !== 'undefined' && document.referrer
          ? document.referrer
          : undefined,
    },
  };

  // Add form_session_id if provided
  if (formSessionId) {
    payload.form_session_id = formSessionId;
  }

  return payload;
}

/**
 * Push event to dataLayer
 */
function pushToDataLayer(payload: GTMEventPayload): void {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  window.dataLayer.push(payload as unknown as Record<string, unknown>);
}

/**
 * Flush queued events to dataLayer
 */
function flushEventQueue(): void {
  while (state.eventQueue.length > 0) {
    const event = state.eventQueue.shift();
    if (event) {
      pushToDataLayer(event);
    }
  }
}

/**
 * Track a GTM event with attribution data
 * Events are queued if GTM isn't ready yet
 * Supports pre-captured data for delayed event firing
 */
export function trackGTMEvent(
  eventName: GTMEventName,
  instanceId: string,
  personData?: { name?: string; email?: string },
  capturedData?: GTMCapturedData,
  formSessionId?: string
): void {
  if (!state.config.enabled) return;

  const payload = buildEventPayload(
    eventName,
    instanceId,
    personData,
    capturedData,
    formSessionId
  );

  if (state.gtmAvailable && isGTMAvailable()) {
    pushToDataLayer(payload);
  } else {
    // Queue event for when GTM becomes available
    state.eventQueue.push(payload);

    // If GTM should be available but isn't detected yet, retry once after a delay
    if (!state.gtmAvailable && state.config.loadIfMissing) {
      setTimeout(() => {
        if (isGTMAvailable()) {
          state.gtmAvailable = true;
          flushEventQueue();
        }
      }, 1000);
    }
  }
}

/**
 * Track widget_loaded event
 * Fires when the user opens the widget
 */
export function trackWidgetLoaded(
  instanceId: string,
  capturedData?: GTMCapturedData
): void {
  trackGTMEvent('widget_loaded', instanceId, undefined, capturedData);
}

/**
 * Track chat_started event (qualified lead - form submitted and backend confirmed)
 * Only fires after backend classification confirms legitimate booking intent
 */
export function trackChatStarted(
  instanceId: string,
  capturedData?: GTMCapturedData
): void {
  trackGTMEvent('chat_started', instanceId, undefined, capturedData);
}

/**
 * Track lead_generated event (qualified lead - conversation created)
 * Only fires after backend classification confirms legitimate booking intent
 */
export function trackLeadGenerated(
  instanceId: string,
  personData: { name?: string; email?: string },
  capturedData?: GTMCapturedData
): void {
  trackGTMEvent('lead_generated', instanceId, personData, capturedData);
}

/**
 * Track form_filling_started event
 * Fires when user starts typing in the form (first interaction)
 * No spam filtering - fires immediately on first input
 * Pushes "form_filling_started" to dataLayer (GTM-compliant)
 */
export function trackFormFillingStarted(
  instanceId: string,
  formSessionId: string
): void {
  trackGTMEvent(
    'form_filling_started',
    instanceId,
    undefined,
    undefined,
    formSessionId
  );
}

/**
 * Track form_submitted event (qualified submission)
 * Only fires after backend classification confirms legitimate booking intent
 * Used for Google Ads conversion tracking on inline forms
 * Pushes "form_submitted" to dataLayer (GTM-compliant)
 */
export function trackFormSubmitted(
  instanceId: string,
  personData: { name?: string; email?: string },
  capturedData?: GTMCapturedData,
  formSessionId?: string
): void {
  trackGTMEvent(
    'form_submitted',
    instanceId,
    personData,
    capturedData,
    formSessionId
  );
}

/**
 * Track enquiry_submitted event
 * Fires when the enquiry form is submitted (alongside form_submitted)
 * Pushes "enquiry_submitted" to dataLayer (GTM-compliant)
 */
export function trackEnquirySubmitted(
  instanceId: string,
  personData: { name?: string; email?: string },
  capturedData?: GTMCapturedData,
  formSessionId?: string
): void {
  trackGTMEvent(
    'enquiry_submitted',
    instanceId,
    personData,
    capturedData,
    formSessionId
  );
}

/**
 * Track widget_message_submitted event
 * Fires when a user sends a chat message in the widget
 * Pushes "widget_message_submitted" to dataLayer (GTM-compliant)
 */
export function trackWidgetMessageSubmitted(
  instanceId: string,
  capturedData?: GTMCapturedData
): void {
  trackGTMEvent(
    'widget_message_submitted',
    instanceId,
    undefined,
    capturedData
  );
}

/**
 */
export function isGTMEnabled(): boolean {
  return state.config.enabled;
}

/**
 * Get current GTM state (for debugging)
 */
export function getGTMState(): Readonly<GTMState> {
  return { ...state };
}

/**
 * Reset GTM state (for testing)
 */
export function resetGTMState(): void {
  state.initialized = false;
  state.gtmAvailable = false;
  state.eventQueue = [];
  state.config = {
    enabled: false,
    loadIfMissing: false,
  };
}
